// lesson-lookup.js — reverse-derive an item's owning lesson from its id (Brief 142).
//
// The audio / media screens normally learn their lesson context from the
// `active_lesson` setting that lesson-detail writes on entry. A DEEP LINK
// (`/exercise/<id>` opened from a shared URL or bookmark, with no prior
// lesson-detail visit) never writes that setting, so the screen has only an
// itemId and no way to find the lesson — leaving the playcount badge stuck at
// the floor-1 default instead of the authored `requiredPlay` (Brief 142
// symptom: item 103520 rendered 0/1 instead of 0/3).
//
// `content/kfe_item_lesson.json` (emitted by tools/build-pathways.mjs) is a
// flat { pathwaySlug → { bareItemId → lessonId } } map built from the same
// canonical walk as the lesson JSONs. This module fetches + memoises it once
// and answers item → { lessonId, pathwayId } lookups from memory. One small
// cached fetch per session — far cheaper than scanning 665 per-lesson files.

const CONTENT_BASE = "/content";

/** @typedef {Record<string, Record<string, string>>} ItemLessonIndex */

/** @type {ItemLessonIndex | null} */
let _index = null;
/** @type {Promise<ItemLessonIndex> | null} */
let _inflight = null;

/**
 * Load + memoise the item→lesson index. Shared across calls in a session.
 * @returns {Promise<ItemLessonIndex>}
 */
async function loadIndex() {
  if (_index) return _index;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    const res = await fetch(`${CONTENT_BASE}/kfe_item_lesson.json`);
    if (!res.ok) throw new Error(`item-lesson index fetch failed: ${res.status}`);
    _index = /** @type {ItemLessonIndex} */ (await res.json());
    return _index;
  })();
  try {
    return await _inflight;
  } finally {
    _inflight = null;
  }
}

/**
 * Reverse-derive the lesson (and owning pathway) that contains `itemId`.
 * Guarded: returns null on any fetch failure or unknown item rather than
 * throwing, so a missing index degrades to the screen's existing fallback.
 *
 * @param {string|number} itemId  bare numeric item id (or "item_NNN" form).
 * @returns {Promise<{ lessonId: string, pathwayId: string } | null>}
 */
export async function findLessonForItem(itemId) {
  const key = String(itemId ?? "").replace(/^(item_|gate_)/, "");
  if (!key) return null;
  let index;
  try {
    index = await loadIndex();
  } catch {
    return null;
  }
  for (const pathwayId of Object.keys(index)) {
    const lessonId = index[pathwayId]?.[key];
    if (lessonId) return { lessonId, pathwayId };
  }
  return null;
}

/**
 * Test-only: clear the in-memory cache so each test starts clean. No-op in
 * production.
 */
export function _resetLessonLookupCache() {
  _index = null;
  _inflight = null;
}
